// src/utils/transform_codegen.js
// Auto-detect array usage and generate Velocity / Python transforms with loop when appropriate.
//
// Exports: buildTransformCode(rule)
// rule shape (example):
// {
//   language: "velocity" | "python",
//   conditions: [ { type, path?, left?, right?, value?, rightIsPath?, valueIsPath? } ],
//   action: { op, path, newKey?, value?, valueIsPath? }
// }

function stripDollar(s = "") {
  return s.replace(/^\$/, "");
}

/* -------------------
   Path utilities
   ------------------- */

function convertSegmentsToPython(segmentString) {
  if (!segmentString) return "";
  const parts = [];
  const dotSplit = segmentString.split(".");
  for (const p of dotSplit) {
    let cur = p;
    const nameMatch = cur.match(/^([^\[\]]+)/);
    if (nameMatch) {
      parts.push(`['${nameMatch[1]}']`);
      cur = cur.substring(nameMatch[1].length);
    }
    const indexRegex = /\[(\d+)\]/g;
    let m;
    while ((m = indexRegex.exec(cur)) !== null) {
      parts.push(`[${m[1]}]`);
    }
  }
  return parts.join("");
}

function velPathToExpr(p = "") {
  if (!p) return "";
  return p; // assume UI generated $document.x or $Alias.x already
}

function aliasToPythonVar(alias) {
  const name = stripDollar(alias);
  return name.charAt(0).toLowerCase() + name.slice(1);
}

function pyPathToExpr(p = "", loop = null) {
  if (!p) return "";
  let working = p.trim();

  if (working === "$document") return "document";

  // If loop alias usage (e.g. $AsnLine.Field) and loop present
  if (loop && working.startsWith(loop.as + ".")) {
    const rest = working.substring(loop.as.length + 1);
    const aliasVar = aliasToPythonVar(loop.as);
    return aliasVar + convertSegmentsToPython(rest);
  }

  if (working.startsWith("$document.")) {
    const rest = working.substring("$document.".length);
    return "document" + convertSegmentsToPython(rest);
  }

  // fallback: variable-style path (e.g. $SomeVar)
  if (working.startsWith("$")) {
    return stripDollar(working);
  }

  return working;
}

/* -------------------
   Literal helpers
   ------------------- */

function literalToVelocity(v) {
  if (v === null) return "null";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "number") return String(v);
  return `"${String(v).replace(/"/g, '\\"')}"`;
}

function literalToPython(v) {
  if (v === null) return "None";
  if (typeof v === "boolean") return v ? "True" : "False";
  if (typeof v === "number") return String(v);
  return `'${String(v).replace(/'/g, "\\'")}'`;
}

/* -------------------
   Loop detection + rewriting
   ------------------- */

// Detect first array-like reference in rule.conditions or rule.action.
// Patterns: $document.<ArrayName>[0], $document.<ArrayName>.<field>, or $document.<ArrayName>
function detectArrayLoop(rule) {
  if (!rule) return null;
  const candidates = [];

  function pushIfArrayPath(p) {
    if (!p || typeof p !== "string") return;
    // match $document.Name or $document.Name[0] or $document.Name.something
    const m = p.match(/^\$document\.([A-Za-z0-9_]+)(?:\[\d+\])?(?:\..*)?$/);
    if (m) {
      candidates.push(m[1]);
    }
  }

  if (Array.isArray(rule.conditions)) {
    for (const c of rule.conditions) {
      pushIfArrayPath(c.path);
      pushIfArrayPath(c.left);
      if (c.rightIsPath) pushIfArrayPath(c.right);
      if (c.valueIsPath) pushIfArrayPath(c.value);
    }
  }

  if (rule.action) {
    pushIfArrayPath(rule.action.path);
    if (rule.action.valueIsPath) pushIfArrayPath(rule.action.value);
  }

  if (candidates.length === 0) return null;
  // choose first unique candidate
  const arrName = [...new Set(candidates)][0];
  return { path: `$document.${arrName}`, as: `$${arrName}` };
}

// Replace occurrences of $document.Array[0].Field or $document.Array.Field with loop alias ($Alias.Field)
// Returns a deep copy of conds/action with replaced path strings.
function rewritePathsForLoop(rule, loop) {
  if (!loop) return rule;

  const loopRootRegex = new RegExp(`\\$document\\.${loop.as.replace(/^\$/, "")}(?:\\[\\d+\\])?`, "g");
  function rewriteStringPath(str) {
    if (!str || typeof str !== "string") return str;
    return str.replace(loopRootRegex, loop.as);
  }

  const copy = JSON.parse(JSON.stringify(rule)); // shallow deep copy
  if (Array.isArray(copy.conditions)) {
    for (const c of copy.conditions) {
      if (c.path) c.path = rewriteStringPath(c.path);
      if (c.left) c.left = rewriteStringPath(c.left);
      if (c.right && c.rightIsPath) c.right = rewriteStringPath(c.right);
      if (c.value && c.valueIsPath) c.value = rewriteStringPath(c.value);
    }
  }
  if (copy.action) {
    if (copy.action.path) copy.action.path = rewriteStringPath(copy.action.path);
    if (copy.action.value && copy.action.valueIsPath) copy.action.value = rewriteStringPath(copy.action.value);
  }
  return copy;
}

/* -------------------
   Condition generators
   ------------------- */

function condsToVelocity(conds = [], loop = null) {
  const parts = [];
  for (const c of conds) {
    const t = (c.type || "").toLowerCase();
    switch (t) {
      case "isnull":
        parts.push(`!${velPathToExpr(c.path)}`);
        break;
      case "notnull":
      case "exists":
        parts.push(`${velPathToExpr(c.path)} != null`);
        break;
      case "equals": {
        const left = velPathToExpr(c.left || c.path);
        const right = c.rightIsPath ? velPathToExpr(c.right) : literalToVelocity(c.right);
        parts.push(`${left} == ${right}`);
        break;
      }
      case "notequals": {
        const left = velPathToExpr(c.left || c.path);
        const right = c.rightIsPath ? velPathToExpr(c.right) : literalToVelocity(c.right);
        parts.push(`${left} != ${right}`);
        break;
      }
      case "comparepaths": {
        const left = velPathToExpr(c.left);
        const right = velPathToExpr(c.right);
        const op = c.op || "==";
        parts.push(`${left} ${op} ${right}`);
        break;
      }
      case "contains": {
        const pathExpr = velPathToExpr(c.path);
        const valExpr = c.valueIsPath ? velPathToExpr(c.value) : literalToVelocity(c.value);
        parts.push(`(${pathExpr} && $util.contains(${pathExpr}, ${valExpr}))`);
        break;
      }
      default:
        parts.push("true");
        break;
    }
  }
  return parts.join(" && ");
}

function condsToPython(conds = [], loop = null) {
  const parts = [];
  for (const c of conds) {
    const t = (c.type || "").toLowerCase();
    switch (t) {
      case "isnull": {
        const p = pyPathToExpr(c.path, loop);
        parts.push(`${p} is None`);
        break;
      }
      case "notnull":
      case "exists": {
        const p = pyPathToExpr(c.path, loop);
        parts.push(`${p} is not None`);
        break;
      }
      case "equals": {
        const left = pyPathToExpr(c.left || c.path, loop);
        const right = c.rightIsPath ? pyPathToExpr(c.right, loop) : literalToPython(c.right);
        parts.push(`${left} == ${right}`);
        break;
      }
      case "notequals": {
        const left = pyPathToExpr(c.left || c.path, loop);
        const right = c.rightIsPath ? pyPathToExpr(c.right, loop) : literalToPython(c.right);
        parts.push(`${left} != ${right}`);
        break;
      }
      case "comparepaths": {
        const left = pyPathToExpr(c.left, loop);
        const right = pyPathToExpr(c.right, loop);
        const op = c.op || "==";
        parts.push(`${left} ${op} ${right}`);
        break;
      }
      case "contains": {
        const p = pyPathToExpr(c.path, loop);
        const val = c.valueIsPath ? pyPathToExpr(c.value, loop) : literalToPython(c.value);
        parts.push(`${val} in ${p}`);
        break;
      }
      default:
        parts.push("True");
        break;
    }
  }
  return parts.join(" and ");
}

/* -------------------
   Action generators
   ------------------- */

function actionToVelocity(action = {}, loop = null) {
  if (!action || !action.op) return "";
  const op = (action.op || "").toLowerCase();

  // helper to compute parent expression (velocity)
  const parentExpr = velPathToExpr(action.path || "$document"); // e.g. "$document" or "$document.AsnId"

  // value expression (either path or literal)
  const valueExpr = action.valueIsPath ? velPathToExpr(action.value) : literalToVelocity(action.value);

  // split "$document.AsnId" -> { parent: "$document", key: "AsnId" }
  function splitParentAndKey(targetPath) {
    if (!targetPath || typeof targetPath !== "string") return { parent: targetPath, key: null };
    // find last dot (simple; handles standard dot paths and ignores bracket indices)
    const lastDot = targetPath.lastIndexOf(".");
    if (lastDot === -1) return { parent: targetPath, key: null };
    const parent = targetPath.substring(0, lastDot);
    const key = targetPath.substring(lastDot + 1);
    return { parent, key };
  }

  // helper: decide whether to use map-style put/remove (for $document or any $alias)
  function prefersMapStyle(parent) {
    return parent && typeof parent === "string" && parent.startsWith("$");
  }

  if (op === "add") {
    const key = action.newKey || "newKey";
    if (prefersMapStyle(parentExpr)) {
      // #set($temp = $document.put("key", value))
      return `#set($temp = ${parentExpr}.put("${key}", ${valueExpr}))`;
    }
    // fallback: assign property
    return `#set(${parentExpr}.${key} = ${valueExpr})`;
  }

  if (op === "update") {
    // action.path might be a full target like "$document.AsnId" or a parent. Use splitParentAndKey to find key.
    const target = action.path || parentExpr;
    const { parent, key } = splitParentAndKey(target);
    const finalParent = parent || parentExpr;
    const finalKey = key || action.newKey || action.key || null;

    if (finalKey && prefersMapStyle(finalParent)) {
      // use put on parent
      return `#set($temp = ${finalParent}.put("${finalKey}", ${valueExpr}))`;
    }

    // fallback to direct assignment when we could not use put
    if (action.path) {
      return `#set(${velPathToExpr(action.path)} = ${valueExpr})`;
    }
    if (finalKey && finalParent) {
      return `#set(${finalParent}.${finalKey} = ${valueExpr})`;
    }

    return "";
  }

  if (op === "remove") {
    // For remove we want map-style remove where possible
    const target = action.path || parentExpr;
    const { parent, key } = splitParentAndKey(target);
    const finalParent = parent || parentExpr;
    const finalKey = key || action.newKey || action.key || null;

    if (finalKey && prefersMapStyle(finalParent)) {
      // #set($temp = $document.remove("AsnId"))
      return `#set($temp = ${finalParent}.remove("${finalKey}"))`;
    }

    // fallback: set to null
    const targetExpr = action.path ? velPathToExpr(action.path) : `${finalParent}.${finalKey}`;
    return `#set(${targetExpr} = null)`;
  }

  return "";
}


function actionToPython(action = {}, loop = null, indent = "") {
  if (!action || !action.op) return [indent + "# (no action)"];
  const op = (action.op || "").toLowerCase();

  // compute python parent/target expressions
  // parent expression in python form (e.g. document or alias var or document['X'])
  const parentExpr = pyPathToExpr(action.path || "$document", loop);

  // If action.path is a full target like document['AsnId'], extract parent and key
  function splitParentAndKeyPython(pyExpr, loop) {
    // pyExpr might look like "document['AsnId']" or "document['Nested']['Key']" or "asnLine['AsnId']"
    // try to parse by matching trailing bracket ['key'] pattern
    const m = pyExpr.match(/^(.*)\['([^']+)'\]$/);
    if (m) {
      return { parent: m[1], key: m[2] };
    }
    // no bracket form — try to treat as variable (no key)
    return { parent: pyExpr, key: null };
  }

  const valueExpr = action.valueIsPath ? pyPathToExpr(action.value, loop) : literalToPython(action.value);

  if (op === "add") {
    const parent = pyPathToExpr(action.path || "$document", loop);
    const key = action.newKey || "newKey";
    return [indent + `${parent}['${key}'] = ${valueExpr}`];
  }

  if (op === "update") {
    // If action.path is target e.g. "$document.AsnId" -> pyPathToExpr returns document['AsnId']
    const targetExpr = pyPathToExpr(action.path || "$document", loop);
    const { parent, key } = splitParentAndKeyPython(targetExpr, loop);

    if (key && parent) {
      // update via parent['key'] = value
      return [indent + `${parent}['${key}'] = ${valueExpr}`];
    }

    // fallback: direct assignment to target expression
    return [indent + `${targetExpr} = ${valueExpr}`];
  }

  if (op === "remove") {
    // Use pop(key, None) inside a guard similar to previous behavior
    const targetExpr = pyPathToExpr(action.path || "$document", loop);
    const { parent, key } = splitParentAndKeyPython(targetExpr, loop);

    if (key && parent) {
      // produce:
      // if 'AsnId' in document:
      //     document.pop('AsnId', None)
      return [
        indent + `if '${key}' in ${parent}:`,
        indent + `    ${parent}.pop('${key}', None)`
      ];
    }

    // fallback: set None
    return [indent + `${targetExpr} = None`];
  }

  return [indent + "# (unknown op)"];
}

/* -------------------
   Top-level generation
   ------------------- */

export function buildTransformCode(rule) {
  if (!rule) return "";
  const lang = (rule.language || "velocity").toLowerCase();

  // detect loop automatically
  const detectedLoop = detectArrayLoop(rule);
  // if loop detected, rewrite paths inside conditions/action to use alias for body generation
  const rewritten = detectedLoop ? rewritePathsForLoop(rule, detectedLoop) : rule;

  if (lang === "python") return generatePythonTransform(rewritten, detectedLoop);
  return generateVelocityTransform(rewritten, detectedLoop);
}

export default buildTransformCode;

/* -------------------
   Velocity generation
   ------------------- */

function generateVelocityTransform(rule, loop) {
  const conds = Array.isArray(rule.conditions) ? rule.conditions : [];
  const action = rule.action || null;
  const lines = [];

  if (loop && loop.path) {
    lines.push(`#foreach(${loop.as} in ${velPathToExpr(loop.path)})`);
    const condExpr = condsToVelocity(conds, loop);
    if (condExpr) {
      lines.push(`\t#if(${condExpr})`);
      const act = actionToVelocity(action, loop);
      lines.push(`\t\t${act || `## (no key provided for ${action ? action.op : "action"})`}`);
      lines.push(`\t#end`);
    } else {
      const act = actionToVelocity(action, loop);
      lines.push(`\t${act || `## (no key provided for ${action ? action.op : "action"})`}`);
    }
    lines.push(`#end`);
  } else {
    const condExpr = condsToVelocity(conds, null);
    lines.push(`#if(${condExpr || "true"})`);
    const act = actionToVelocity(action, null);
    lines.push(`${act || `## (no key provided for ${action ? action.op : "action"})`}`);
    lines.push(`#end`);
  }

  return lines.join("\n");
}

/* -------------------
   Python generation
   ------------------- */

function generatePythonTransform(rule, loop) {
  const conds = Array.isArray(rule.conditions) ? rule.conditions : [];
  const action = rule.action || null;
  const lines = [];

  lines.push(`# Generated Python code`);
  if (loop && loop.path) {
    const pyLoopTarget = pyPathToExpr(loop.path, null); // e.g. document['AsnLine']
    const alias = aliasToPythonVar(loop.as);
    lines.push(`for ${alias} in ${pyLoopTarget}:`);
    const condExpr = condsToPython(conds, loop);
    if (condExpr) {
      lines.push(indent(1) + `if ${condExpr}:`);
      const actLines = actionToPython(action, loop, indent(2));
      lines.push(...actLines);
    } else {
      const actLines = actionToPython(action, loop, indent(1));
      lines.push(...actLines);
    }
  } else {
    const condExpr = condsToPython(conds, null);
    if (condExpr) {
      lines.push(`if ${condExpr}:`);
      const actLines = actionToPython(action, null, indent(1));
      lines.push(...actLines);
    } else {
      lines.push(`# No conditions -> no-op`);
    }
  }

  return lines.join("\n") + "\n";
}

/* -------------------
   small util
   ------------------- */

function indent(level) {
  return "    ".repeat(level);
}
